import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { WarningOctagon, Info } from "@phosphor-icons/react";

const MODES = ["catalog_only","verified_research_checkout","licensed_pharmacy_referral","hybrid"];

export default function AdminSiteSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/site-settings", patch);
      setSettings(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return (
    <AdminGuard><AdminNav /><div className="max-w-4xl mx-auto px-4 py-10 text-brand-muted">Loading…</div></AdminGuard>
  );

  const isSuper = user?.role === "super_admin";

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-10" data-testid="admin-site-settings">
        <div>
          <div className="overline mb-2">System</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Site Settings</h1>
        </div>

        {/* Commerce mode */}
        <section className="border border-brand-border bg-white p-6">
          <div className="overline mb-3">Commerce mode <span className="text-red-300">· super_admin only</span></div>
          <div className="border border-amber-500/40 bg-amber-500/5 p-4 mb-4 flex items-start gap-3 text-xs text-amber-200 font-mono leading-relaxed">
            <WarningOctagon size={16} weight="duotone" className="text-amber-300 mt-0.5" />
            <span>Changing this activates or deactivates live payment processing. Do not switch away from <span className="text-amber-100">catalog_only</span> until legal, processor, and compliance review are complete for every product.</span>
          </div>
          <Select value={settings.commerce_mode} onValueChange={(v) => isSuper && save({ commerce_mode: v })}>
            <SelectTrigger data-testid="settings-commerce-mode" disabled={!isSuper}><SelectValue /></SelectTrigger>
            <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </section>

        {/* Announcement bar */}
        <section className="border border-brand-border bg-white p-6 space-y-4">
          <div className="overline">Announcement bar</div>
          <div className="flex items-center justify-between">
            <Label>Active</Label>
            <Switch checked={!!settings.announcement_bar_active} onCheckedChange={(v) => save({ announcement_bar_active: !!v })} data-testid="settings-announcement-active" />
          </div>
          <div>
            <Label>Text</Label>
            <Textarea defaultValue={settings.announcement_bar_text} onBlur={(e) => save({ announcement_bar_text: e.target.value })} data-testid="settings-announcement-text" />
          </div>
        </section>

        {/* Email */}
        <section className="border border-brand-border bg-white p-6 space-y-4">
          <div className="overline">Transactional email</div>
          <div className="flex items-center justify-between">
            <Label>Live sending enabled</Label>
            <Switch checked={!!settings.email_delivery_enabled} onCheckedChange={(v) => save({ email_delivery_enabled: !!v })} data-testid="settings-email-enabled" />
          </div>
          <div>
            <Label>From email</Label>
            <Input defaultValue={settings.resend_from_email} onBlur={(e) => save({ resend_from_email: e.target.value })} data-testid="settings-from-email" />
          </div>
          <div>
            <Label>Admin notification email</Label>
            <Input defaultValue={settings.admin_notification_email} onBlur={(e) => save({ admin_notification_email: e.target.value })} data-testid="settings-admin-email" />
          </div>
          <div className="text-[11px] font-mono text-brand-muted flex items-center gap-2">
            <Info size={12} weight="duotone" className="text-brand-accent" />
            <span>The RESEND_API_KEY itself is not editable here — rotate it via the environment.</span>
          </div>
        </section>

        {/* Analytics */}
        <section className="border border-brand-border bg-white p-6 space-y-4">
          <div className="overline">Analytics & search</div>
          <div>
            <Label>GA4 Measurement ID</Label>
            <Input defaultValue={settings.ga4_measurement_id} placeholder="G-XXXXXXX" onBlur={(e) => save({ ga4_measurement_id: e.target.value })} data-testid="settings-ga4" />
          </div>
          <div>
            <Label>GTM Container ID</Label>
            <Input defaultValue={settings.gtm_container_id} placeholder="GTM-XXXXXXX" onBlur={(e) => save({ gtm_container_id: e.target.value })} data-testid="settings-gtm" />
          </div>
          <div>
            <Label>Search Console verification token</Label>
            <Input defaultValue={settings.search_console_verification} onBlur={(e) => save({ search_console_verification: e.target.value })} data-testid="settings-search-console" />
          </div>
        </section>

        {/* Turnstile */}
        <section className="border border-brand-border bg-white p-6 space-y-4">
          <div className="overline">Cloudflare Turnstile (bot protection)</div>
          <div>
            <Label>Site key</Label>
            <Input defaultValue={settings.turnstile_site_key} onBlur={(e) => save({ turnstile_site_key: e.target.value })} data-testid="settings-turnstile" />
          </div>
          <div className="text-[11px] font-mono text-brand-muted flex items-center gap-2">
            <Info size={12} weight="duotone" className="text-brand-accent" />
            <span>Widgets render only when a site key is configured; secret key is stored in the environment.</span>
          </div>
        </section>
      </div>
    </AdminGuard>
  );
}
